import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CRUDList from '../Common/CRUDList';
import DateInput from '../Common/DateInput';
import { bookings, agents, carriers } from '../../services/api';
import { formatDate } from '../../utils/dateFormat';
import { ticketStatuses, ticketStatusColors } from '../../constants/travelEnums';
const changeByTypes = { 10: 'Carrier', 20: 'Passenger' };

function BookingForm({ formData, setFormData }) {
  const [agentsList, setAgentsList] = useState([]);
  const [carriersList, setCarriersList] = useState([]);
  const [bookingsList, setBookingsList] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingReplacementId, setPendingReplacementId] = useState(null);
  const isReplacement = formData.booking_init_id != null;

  useEffect(() => {
    loadAgents();
    loadCarriers();
    loadBookings();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await agents.getAll();
      setAgentsList(response.data);
    } catch (err) {
      console.error('Failed to load agents', err);
    }
  };

  const loadCarriers = async () => {
    try {
      const response = await carriers.getAll();
      setCarriersList(response.data);
    } catch (err) {
      console.error('Failed to load carriers', err);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await bookings.getAll();
      setBookingsList(response.data);
    } catch (err) {
      console.error('Failed to load bookings', err);
    }
  };

  const handleReplacementChange = (e) => {
    const replacedId = e.target.value ? parseInt(e.target.value) : null;
    if (replacedId) {
      setPendingReplacementId(replacedId);
      setShowConfirmModal(true);
    } else {
      setFormData({ ...formData, replaced_booking_id: null, booking_init_id: null, booking_chg_num: 0, prev_change_by: null });
    }
  };

  const confirmReplacement = () => {
    const replacedId = pendingReplacementId;
    const replacedBooking = bookingsList.find(b => b.booking_id === replacedId);
    const initId = replacedBooking?.booking_init_id || replacedId;
    const chainBookings = bookingsList.filter(b =>
      b.booking_init_id === initId || b.booking_id === initId
    );
    const maxChgNum = Math.max(...chainBookings.map(b => b.booking_chg_num || 0));
    setFormData({
      ...formData,
      replaced_booking_id: replacedId,
      booking_init_id: initId,
      booking_chg_num: maxChgNum + 1,
      booking_agent_id: replacedBooking?.booking_agent_id || formData.booking_agent_id,
      booking_carrier_id: replacedBooking?.booking_carrier_id || formData.booking_carrier_id,
      booking_parent_id: replacedBooking?.booking_parent_id || formData.booking_parent_id,
      prev_change_by: 10
    });
    setShowConfirmModal(false);
    setPendingReplacementId(null);
  };

  const cancelReplacement = () => {
    setShowConfirmModal(false);
    setPendingReplacementId(null);
  };

  const getSelectedBookingLabel = () => {
    if (pendingReplacementId) {
      const b = bookingsList.find(b => b.booking_id === pendingReplacementId);
      if (b) return `${b.booking_pnr} (${b.booking_date})`;
    }
    return '';
  };

  return (
    <>
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Replacement</h3>
            <p>Are you sure you want to replace booking <strong>{getSelectedBookingLabel()}</strong>? This action will mark the original booking as "Replaced".</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={cancelReplacement}>No</button>
              <button type="button" className="btn btn-danger" onClick={confirmReplacement}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {!formData.booking_id && (
        <div className="form-group replacement-warning">
          <label>Original Booking (for replacement)</label>
          <select
            value={formData.replaced_booking_id || ''}
            onChange={handleReplacementChange}
          >
            <option value="">None (original booking)</option>
            {bookingsList
              .filter(b => b.booking_status === 10)
              .map(b => (
                <option key={b.booking_id} value={b.booking_id}>
                  {b.booking_pnr} ({b.booking_date})
                </option>
              ))}
          </select>
          {isReplacement && (
            <small className="text-muted">Change #: {formData.booking_chg_num}</small>
          )}
        </div>
      )}

      {isReplacement && !formData.booking_id && (
        <div className="form-group">
          <label>Changed By *</label>
          <select
            value={formData.prev_change_by || ''}
            onChange={(e) => setFormData({ ...formData, prev_change_by: parseInt(e.target.value) })}
            required
          >
            <option value="10">Carrier</option>
            <option value="20">Passenger</option>
          </select>
        </div>
      )}

      <div className="form-group">
        <label>Booking Date *</label>
        <DateInput
          value={formData.booking_date || ''}
          onChange={(value) => setFormData({ ...formData, booking_date: value })}
          required
          showTodayButton
        />
      </div>

      <div className="form-group">
        <label>PNR (Record Locator) *</label>
        <input
          type="text"
          value={formData.booking_pnr || ''}
          onChange={(e) => setFormData({ ...formData, booking_pnr: e.target.value.toUpperCase() })}
          required
        />
      </div>

      <div className="form-group">
        <label>Parent Booking (for nested bookings)</label>
        <select
          value={formData.booking_parent_id || ''}
          onChange={(e) => setFormData({ ...formData, booking_parent_id: e.target.value ? parseInt(e.target.value) : null })}
        >
          <option value="">None (root booking)</option>
          {bookingsList.filter(b => !b.booking_parent_id).map(b => (
            <option key={b.booking_id} value={b.booking_id}>
              {b.booking_pnr} ({b.booking_date})
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Agent</label>
        <select
          value={formData.booking_agent_id || ''}
          onChange={(e) => setFormData({ ...formData, booking_agent_id: e.target.value ? parseInt(e.target.value) : null })}
        >
          <option value="">Select agent</option>
          {agentsList.map(agent => (
            <option key={agent.agent_id} value={agent.agent_id}>
              {agent.agent_name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Carrier</label>
        <select
          value={formData.booking_carrier_id || ''}
          onChange={(e) => setFormData({ ...formData, booking_carrier_id: e.target.value ? parseInt(e.target.value) : null })}
        >
          <option value="">Select carrier</option>
          {carriersList.map(carrier => (
            <option key={carrier.carrier_id} value={carrier.carrier_id}>
              {carrier.carrier_name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Status *</label>
        <select
          value={formData.booking_status || ''}
          onChange={(e) => setFormData({ ...formData, booking_status: parseInt(e.target.value) })}
          required
        >
          <option value="">Select status</option>
          <option value="10">Active</option>
          <option value="20">Replaced</option>
          <option value="30">Cancelled</option>
        </select>
      </div>

      {(formData.booking_status === 20 || formData.booking_status === 30) && (
        <>
          <div className="form-group">
            <label>Change Date</label>
            <DateInput
              value={formData.booking_change_date || ''}
              onChange={(value) => setFormData({ ...formData, booking_change_date: value })}
            />
          </div>

          <div className="form-group">
            <label>Changed By</label>
            <select
              value={formData.booking_change_by || ''}
              onChange={(e) => setFormData({ ...formData, booking_change_by: e.target.value ? parseInt(e.target.value) : null })}
            >
              <option value="">Select who changed</option>
              <option value="10">Carrier</option>
              <option value="20">Passenger</option>
            </select>
          </div>
        </>
      )}
    </>
  );
}

function BookingsList() {
  const navigate = useNavigate();

  return (
    <CRUDList
      title="Bookings"
      api={bookings}
      columns={['ID', 'PNR', 'Date', 'Parent', 'Agent', 'Carrier', 'Status', 'Actions']}
      FormComponent={BookingForm}
      defaultFormData={{
        replaced_booking_id: null,
        booking_init_id: null,
        booking_chg_num: 0,
        booking_date: '',
        booking_pnr: '',
        booking_parent_id: null,
        booking_agent_id: null,
        booking_carrier_id: null,
        booking_status: 10,
        booking_change_date: null,
        booking_change_by: null,
        prev_change_by: null
      }}
      useOldStyle={true}
      renderRow={(item) => (
        <>
          <td>{item.booking_id}</td>
          <td><strong>{item.booking_pnr}</strong></td>
          <td>{formatDate(item.booking_date)}</td>
          <td>{item.parent_pnr || '-'}</td>
          <td>{item.agent_name || '-'}</td>
          <td>{item.carrier_name || '-'}</td>
          <td>
            <span className={`badge ${ticketStatusColors[item.booking_status]}`}>
              {ticketStatuses[item.booking_status]}
            </span>
          </td>
          <td>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => navigate(`/booking-details/${item.booking_id}`)}
              style={{ padding: '4px 8px', fontSize: '0.85em' }}
            >
              View Details
            </button>
          </td>
        </>
      )}
    />
  );
}

export default BookingsList;
